import { useSearchParams } from 'react-router';

/**
 * Custom hook for managing filter state synchronized with URL search parameters
 *
 * @param {Object} initialFilters - Object mapping filter names to their URL param names
 * @returns {Object} Object containing filter values and handler function
 *
 * @example
 * const { filters, handleFilterChange } = useFilterState({
 *   searchText: 'user_search',
 *   provider: 'provider',
 *   status: 'status',
 *   userId: 'user_id'
 * });
 */
export const useFilterState = (initialFilters = {}) => {
	const [searchParams, setSearchParams] = useSearchParams();

	// Build filter values from URL params
	const filters = Object.keys(initialFilters).reduce((acc, key) => {
		const paramName = initialFilters[key];
		acc[key] = searchParams.get(paramName) || '';
		return acc;
	}, {});

	/**
	 * Receives new filter value and updates URL search parameters
	 * @param {string} paramName - The URL parameter name
	 * @param {string} newFilterValue - The new filter value
	 */
	const handleFilterChange = (paramName, newFilterValue) => {
		// Get current URL params
		const newFilterParams = Object.fromEntries(searchParams);

		if (newFilterValue) {
			newFilterParams[paramName] = newFilterValue;
		} else {
			delete newFilterParams[paramName];
		}

		// Reset to page 0 when filters change
		newFilterParams.page = 0;

		setSearchParams(newFilterParams, { replace: true });
	};

	return {
		filters,
		handleFilterChange,
	};
};
